<template>
  <v-form ref="stepCustomerRef">
    <v-row no-gutters>
      <v-col cols="12" md="6" class="pr-md-4">
        <template v-if="isOrdererNewOrExistingEnergyCustomer">
          <h4 class="pb-0 pt-4">
            Anschlusstyp
          </h4>

          <v-radio-group
            v-model="formCustomerStore.connectionType"
            :rules="[
              (value?: ConnectionType) => value !== undefined || 'Dieses Feld wird benötigt!'
            ]"
            hide-details
            inline
          >
            <v-radio label="Anbieterwechsel" :value="ConnectionType.SWITCHING_PROVIDER" />
            <v-radio label="Neuanschluss" :value="ConnectionType.NEW_CONNECTION" />
          </v-radio-group>

          <v-container v-if="formCustomerStore.isSwitchingProvider">
            <v-select
              v-model="formCustomerStore.selectedProvider"
              :items="bookingStore.providers"
              :rules="mandatory"
            >
              <template #label>
                Vorheriger Anbieter <sup v-if="bookingStore.providers" class="required">*</sup>
              </template>
            </v-select>

            <v-text-field
              v-if="formCustomerStore.selectedProvider === 'Anderer'"
              v-model="formCustomerStore.otherProvider"
              :rules="mandatory"
            >
              <template #label>
                Anderer Anbieter <sup v-if="mandatory" class="required">*</sup>
              </template>
            </v-text-field>

            <v-row no-gutters>
              <v-col cols="12" md="3" class="pr-2">
                <v-text-field
                  v-model="formCustomerStore.phoneNumberChange.prefix"
                  :rules="mandatory"
                >
                  <template #label>
                    Vorwahl <sup v-if="mandatory" class="required">*</sup>
                  </template>
                </v-text-field>
              </v-col>

              <v-col cols="12" md="9">
                <v-text-field
                  v-model="formCustomerStore.phoneNumberChange.number"
                  label="Rufnummer bisheriger Anschluss"
                  :rules="mandatory"
                >
                  <template #label>
                    Rufnummer bisheriger Anschluss <sup v-if="mandatory" class="required">*</sup>
                  </template>
                </v-text-field>
              </v-col>
            </v-row>

            <h4 class="pb-0">
              Rufnummernmitnahme
            </h4>

            <v-radio-group v-model="formCustomerStore.numberPortability" hide-details inline>
              <v-radio label="Ja" :value="true" />
              <v-radio label="Nein" :value="false" />
            </v-radio-group>

            <phone-numbers-porting
              v-if="formCustomerStore.numberPortability"
              v-model="formCustomerStore.portablePhoneNumbers"
            />

            <v-checkbox
              v-model="formCustomerStore.otherConnectionOwners"
              label="Abweichender Anschlussinhaber"
              hide-details
            />

            <other-connection-owners
              v-if="formCustomerStore.otherConnectionOwners"
              v-model="formCustomerStore.otherConnectionOwnersData"
            />
          </v-container>

          <v-container class="pl-0" fluid>
            <h4 class="pb-0">
              Einzelverbindungsnachweis
            </h4>

            <v-radio-group
              v-model="formCustomerStore.itemizedBill"
              hide-details
              class="mt-0"
              inline
            >
              <v-radio
                v-for="(item, index) in itemizedBillOptions"
                :key="index"
                :label="item"
                :value="item"
              />
            </v-radio-group>
          </v-container>
        </template>

        <v-container class="pl-0 pt-0" fluid>
          <h4 class="pb-0">
            Telefonbucheintrag
          </h4>

          <v-radio-group
            v-model="formCustomerStore.phoneBookEntry"
            hide-details
            class="mt-0"
            inline
          >
            <v-radio
              v-for="(item, index) in phoneBookEntryOptions"
              :key="index"
              :label="item"
              :value="item"
            />
          </v-radio-group>

          <v-alert v-if="formCustomerStore.phoneBookEntry !== 'Nein'" class="body-2 mt-2 pa-4">
            <div class="title font-weight-medium">
              Bitte informieren
            </div>
            <div>
              Im Falle einer Eintragung in ein öffentliches Teilnehmerverzeichnis
              (Telefonbucheintrag) ist eine Auskunft über Namen und Anschrift des Teilnehmers anhand
              der Rufnummer (Inverssuche) möglich.
            </div>
          </v-alert>
        </v-container>
      </v-col>

      <v-col class="pl-2" cols="12" md="6">
        <h4 class="pb-0">
          Zahlungsart
        </h4>

        <v-radio-group
          v-model="formCustomerStore.wantsBankTransfer"
          hide-details
          class="mt-0 mb-2"
          inline
        >
          <v-radio label="SEPA-Lastschrift" :value="false" />
          <v-radio label="Kunde wünscht Zahlung per Überweisung" :value="true" />
        </v-radio-group>

        <v-text-field
          v-if="!formCustomerStore.wantsBankTransfer"
          v-model="formCustomerStore.iban"
          v-maska="ibanMask"
          :rules="iban"
        >
          <template #label>
            IBAN <sup v-if="iban" class="required">*</sup>
          </template>
        </v-text-field>

        <v-text-field
          v-model="formCustomerStore.customerPassword"
          label="Service-Kennwort"
          :rules="customerPasswordRule"
          class="mb-2"
        >
          <template #label>
            Service-Kennwort <sup v-if="customerPasswordRule" class="required">*</sup>
          </template>
        </v-text-field>

        <v-alert class="body-2 mt-2 pa-4">
          Mit dem persönlichen Kennwort kann der Kunde/die Kundin telefonische Auskünfte einholen
          und Änderungswünsche über mitteln. Dieses Kennwort dient außerdem zum Schutz der
          persönlichen Daten des Kunden/der Kundin. <br><br>Das Kennwort muss
          <b>mindestens 6 und maximal 15 Zeichen</b> lang sein und aus <b>Buchstaben A-Z</b>, sowie
          <b>Zahlen 0-9</b> bestehen. Die
          <b>Sonderzeichen ! * # , ; ? + - _ . = ~ ^ % ( ) : /</b> und <b>Umlaute ä ö ü ß</b> sind
          ebenfalls möglich.
        </v-alert>

        <h4 class="pb-0 mt-6 mb-2">
          Weitere Auftragsdetails
        </h4>

        <v-checkbox
          v-model="formCustomerStore.dataUsage"
          label="Bestandsdatennutzung"
          hide-details
          density="compact"
        />

        <v-checkbox
          v-model="formCustomerStore.extendedDataUsage"
          label="Erweiterte Bestandsdatennutzung"
          hide-details
          density="compact"
        />

        <v-checkbox
          v-model="formCustomerStore.telephoneAdvertisement"
          label="Beratung & Werbung am Telefon"
          hide-details
          density="compact"
        />

        <v-checkbox v-model="formCustomerStore.creditCheck" :rules="mandatory" density="compact">
          <template #label>
            Kunde ist mit Bonitätsprüfung einverstanden
            <sup v-if="mandatory" class="required">*</sup>
          </template>
        </v-checkbox>

        <v-text-field
          v-if="!formOrdererStore.household?.customerId"
          v-model="formCustomerStore.referralCode"
          class="mt-2"
          :rules="formCustomerStore.referralCode?.length ? referralCode : []"
          label="Kunden werben Kunden: Empfehlungscodes"
          counter="14"
          persistent-counter
        />
      </v-col>
    </v-row>
  </v-form>
</template>
<script setup lang="ts">
import type { MaskInputOptions } from 'maska';
import { ref, computed, reactive } from 'vue';
import { ConnectionType } from '../../types/step-data';
import { mandatory, iban, referralCode } from '../../composables/validations';
import useBookingStore from '../../store/booking';
import useFormCustomerStore from '../../store/form-customer';
import useFormOrdererStore from '../../store/form-orderer';
import PhoneNumbersPorting from './phone-numbers-porting.vue';
import OtherConnectionOwners from './other-connection-owners.vue';
import useOrderStore from '../../store/order';

const itemizedBillOptions: string[] = [
  'Nein',
  'komplette Darstellung',
  'verkürzte Darstellung',
  'keine Angabe',
];

const phoneBookEntryOptions: string[] = [
  'Nein',
  'Standard (Name; Adresse; 1. Rufnummer)',
  'gekürzt (Name; 1. Rufnummer)',
];

const bookingStore = useBookingStore();
const formCustomerStore = useFormCustomerStore();
const formOrdererStore = useFormOrdererStore();

const ibanMask = reactive<MaskInputOptions>({
  mask: 'DE## #### #### #### #### ##',
  eager: true,
});

const stepCustomerRef = ref();

const isOrdererNewOrExistingEnergyCustomer = computed(
  () => !formOrdererStore.household?.customerId
    || formOrdererStore.household?.customerId.toString().startsWith('2'),
);

const customerPasswordRule = computed(() => [
  (value: string) => !!value || 'Dieses Feld wird benötigt!',
  (value: string) => !/[^0-9a-zA-ZäÄöÖüÜß!*#,;?+\-_.=~^%():\\/]/.test(value)
      || 'Nur die Sonderzeichen ! * # , ; ? + - _ . = ~ ^ % ( ) : / und Umlaute ä ö ü ß sind ebenfalls möglich.',
  (value: string) => (value && value.length > 5 && value.length < 16)
      || 'Tragen Sie bitte ein mindestens 6 und maximal 15 Zeichen langes persönliches Kennwort ein',
  (value: string) => /[a-zA-Z]/.test(value) || 'Nutzen Sie bitte die Buchstaben A-Z',
  (value: string) => /\d/.test(value) || 'Nutzen Sie bitte die Zahlen 0-9',
]);

const isValid = async () => {
  const { valid } = await stepCustomerRef.value.validate();
  return valid;
};

defineExpose({
  applyData: () => {
    useOrderStore().addCustomerData(formCustomerStore.stepData);
  },
  isValid,
});
</script>
